import type { ContactPaymentMethod } from '@/types/contact';
import { isBankPaymentMethod } from '@/lib/payments/savedPaymentMethods';
import type {
  InvoicingMethod,
  InvoicingMode,
  InvoicingSchedule,
  JobPriority,
  JobType,
  RecurringConfig,
  RecurringDayConstraintMode,
  RecurringFrequency,
  RecurringPatternMode,
  RecurringVisitPlacement,
  RecurringVisitPattern,
  RecurringWeatherPolicy,
} from '@/types/job';
import type { JobType as JobTypeSetting, RecurrenceFrequency } from '@/types/settings';
import { dateToDateString } from '@/lib/date-values';
import { finalizeNameTemplateToken, renderNameTemplate } from '@/lib/name-templates';

export type JobTypeOption = { value: JobType; label: string; aliases?: string[] };
export type JobBookingType = 'ONE_OFF' | 'RECURRING';

export const DEFAULT_JOB_NAME_TEMPLATE = '{customer_name} - {job_type}';

interface JobNameTemplateInput {
  template?: string | null;
  customerName?: string | null;
  jobType?: string | null;
  date?: string | null;
  address?: string | null;
}

export const PRIORITY_OPTIONS: { value: JobPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
  // NOT-296: relabel only — the enum value stays URGENT (job-types use EMERGENCY).
  { value: 'URGENT', label: 'Emergency' },
];

export const INVOICING_METHOD_OPTIONS: { value: InvoicingMethod; label: string }[] = [
  { value: 'PER_VISIT', label: 'Per Visit' },
  { value: 'FIXED_PRICE', label: 'Fixed Price' },
];

export const INVOICING_SCHEDULE_OPTIONS: { value: InvoicingSchedule; label: string }[] = [
  { value: 'ON_COMPLETION', label: 'On Completion' },
  { value: 'LAST_DAY_OF_MONTH', label: 'Last Day of Month' },
  { value: 'CUSTOM_SCHEDULE', label: 'Custom Schedule' },
  { value: 'AS_NEEDED', label: 'As Needed' },
];

/**
 * The invoicing schedules a job form offers. Custom Schedule bills on a cycle
 * the form cannot set, and job types already retired it, so only a series
 * still on it shows it.
 */
export function invoicingScheduleOptionsFor(current: InvoicingSchedule | null | undefined) {
  return INVOICING_SCHEDULE_OPTIONS.filter(
    (option) => option.value !== 'CUSTOM_SCHEDULE' || current === 'CUSTOM_SCHEDULE',
  );
}

/**
 * What a per-visit series' invoices look like on each schedule. The form said
 * "Invoice each visit as it is completed" and "One invoice per completed
 * visit" whatever the schedule, while a month-end or as-needed series puts
 * every visit it bills on one invoice.
 */
export const PER_VISIT_SCHEDULE_COPY: Record<
  InvoicingSchedule,
  { hint: string; totalCaption: string }
> = {
  ON_COMPLETION: {
    hint: 'Invoice each visit as it is completed.',
    totalCaption: 'One invoice per completed visit.',
  },
  LAST_DAY_OF_MONTH: {
    hint: "Invoice the month's completed visits together on its last day.",
    totalCaption: "Each completed visit adds this to the month's invoice.",
  },
  AS_NEEDED: {
    hint: 'Invoice the completed visits together whenever you run billing.',
    totalCaption: 'Each completed visit adds this to the next invoice you run.',
  },
  CUSTOM_SCHEDULE: {
    hint: 'Invoice the completed visits together on the custom schedule.',
    totalCaption: 'Each completed visit adds this to the next scheduled invoice.',
  },
};

export const INVOICING_MODE_OPTIONS: {
  value: InvoicingMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'FIXED_PRICE',
    label: 'Fixed Price',
    description:
      'One set price for the whole job. Invoice the full amount — optionally auto-sent when the job is marked complete.',
  },
  {
    value: 'MILESTONE',
    label: 'Milestone',
    description:
      'Bill the job in stages. Set up a payment schedule and invoice each milestone as it is reached.',
  },
  {
    value: 'MANUAL',
    label: 'Manual',
    description:
      'No automatic invoicing. Create and send invoices yourself whenever you are ready.',
  },
];

export const RECURRING_FREQUENCY_OPTIONS: { value: RecurringFrequency; label: string }[] = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'CUSTOM', label: 'Custom' },
];

export function jobTypeSupportsBookingType(
  jobType: JobTypeSetting,
  bookingType: JobBookingType,
): boolean {
  if (jobType.schedulingDefaults?.availability === 'BOTH') return true;
  return resolveJobTypeBookingType(jobType) === bookingType;
}

export function resolveJobTypeBookingType(jobType: JobTypeSetting): JobBookingType {
  const availability = jobType.schedulingDefaults?.availability;
  if (availability === 'RECURRING') return 'RECURRING';
  if (availability === 'ONE_OFF') return 'ONE_OFF';

  const frequency =
    jobType.schedulingDefaults?.recurring?.frequency ?? jobType.recurrenceFrequency ?? 'NONE';
  return frequency === 'NONE' ? 'ONE_OFF' : 'RECURRING';
}

interface RecurringSchedulingDefaultsForm {
  recurPatternMode: RecurringPatternMode;
  recurVisitsPerCycle: string;
  recurDayConstraintMode: RecurringDayConstraintMode;
  recurAllowedWeekDays: number[];
  recurFrequency: RecurringFrequency;
  recurInterval: string;
  recurIntervalUnit: 'DAYS' | 'WEEKS' | 'MONTHS' | 'YEARS';
  recurWeekDays: number[];
  recurCustomDays: number[];
  recurMonthMode: 'dayOfMonth' | 'dayOfWeek';
  recurMonthDay: number | null;
  recurMonthLastDay: boolean;
  recurMonthDayOfWeek: number | null;
  recurMonthDayOfWeekOccurrence: number;
  recurStartDate: string;
  recurVisitStartTime: string;
  recurVisitEndTime: string;
  recurVisitEndOverride: boolean;
  recurVisitPatterns: RecurringVisitPattern[];
  recurWeatherPolicy?: RecurringWeatherPolicy;
}

const NEUTRAL_RECURRING_SCHEDULING_DEFAULTS = {
  recurPatternMode: 'FIXED' as const,
  recurVisitsPerCycle: '1',
  recurDayConstraintMode: 'ANY' as const,
  recurAllowedWeekDays: [] as number[],
  recurFrequency: 'WEEKLY' as const,
  recurInterval: '1',
  recurIntervalUnit: 'WEEKS' as const,
  recurWeekDays: [] as number[],
  recurCustomDays: [] as number[],
  recurMonthMode: 'dayOfMonth' as const,
  recurMonthDay: null,
  recurMonthLastDay: false,
  recurMonthDayOfWeek: null,
  recurMonthDayOfWeekOccurrence: 1,
  recurVisitStartTime: '09:00',
  recurVisitEndTime: '',
  recurVisitEndOverride: false,
  recurVisitPatterns: [] as RecurringVisitPattern[],
};

function resolveAnnualTemplateStartDate(anchorDate: string, month: number, day: number): string {
  const anchorMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(anchorDate);
  const anchorYear = Number(anchorMatch?.[1]);

  if (
    !anchorMatch ||
    !Number.isInteger(month) ||
    month < 1 ||
    month > 12 ||
    !Number.isInteger(day) ||
    day < 1 ||
    day > 31
  ) {
    return anchorDate;
  }

  for (let year = anchorYear; year <= anchorYear + 8; year += 1) {
    const candidate = new Date(Date.UTC(year, month - 1, day));
    if (
      candidate.getUTCFullYear() !== year ||
      candidate.getUTCMonth() !== month - 1 ||
      candidate.getUTCDate() !== day
    ) {
      continue;
    }

    const candidateDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (candidateDate >= anchorDate) return candidateDate;
  }

  return anchorDate;
}

/**
 * Atomically replaces every recurrence field owned by a Job Type template.
 * Job-specific bounds (start/end contract) remain untouched, while stale fixed
 * weekdays and occurrence patterns are cleared when switching to a flexible
 * or choose-on-create template.
 */
export function applyRecurringJobTypeSchedulingDefaults<T extends RecurringSchedulingDefaultsForm>(
  form: T,
  jobType: JobTypeSetting,
): T {
  const defaults = jobType.schedulingDefaults?.recurring;
  const anchorDate = form.recurStartDate || dateToDateString(new Date());
  const reset = {
    ...form,
    ...NEUTRAL_RECURRING_SCHEDULING_DEFAULTS,
    recurAllowedWeekDays: [],
    recurWeekDays: [],
    recurCustomDays: [],
    recurVisitPatterns: [],
    recurWeatherPolicy: jobType.weatherPolicy ?? undefined,
  };

  if (!defaults || defaults.patternMode === 'CHOOSE_ON_CREATE') {
    return reset as T;
  }

  return {
    ...reset,
    recurPatternMode: defaults.patternMode === 'FLEXIBLE' ? 'FLEXIBLE' : 'FIXED',
    recurVisitsPerCycle: String(Math.max(1, defaults.visitsPerCycle ?? 1)),
    recurDayConstraintMode: defaults.dayConstraintMode ?? 'ANY',
    recurAllowedWeekDays: [...(defaults.allowedWeekDays ?? [])],
    recurFrequency: mapRecurrenceFrequency(defaults.frequency),
    recurInterval: String(Math.max(1, defaults.interval)),
    recurWeekDays: [...(defaults.weekDays ?? [])],
    recurMonthMode: defaults.monthMode === 'DAY_OF_WEEK' ? 'dayOfWeek' : 'dayOfMonth',
    recurMonthDay: defaults.monthDay ?? null,
    recurMonthLastDay: defaults.monthLastDay ?? false,
    recurMonthDayOfWeek: defaults.monthDayOfWeek ?? null,
    recurMonthDayOfWeekOccurrence: defaults.monthDayOfWeekOccurrence ?? 1,
    recurStartDate:
      defaults.frequency === 'YEAR' && defaults.annualMonth && defaults.annualDay
        ? resolveAnnualTemplateStartDate(anchorDate, defaults.annualMonth, defaults.annualDay)
        : form.recurStartDate,
    recurVisitStartTime: defaults.visitStartTime ?? '09:00',
    recurVisitEndTime: defaults.visitEndTime ?? '',
    recurVisitEndOverride: Boolean(defaults.visitEndTime),
    recurVisitPatterns: [],
  } as T;
}

// Recurring spec §3/§7 — pricing_terms.billing_mode. Per visit keeps the
// existing method/schedule; subscription/prepaid introduce a base fee.
export type BillingMode = 'PER_VISIT' | 'SUBSCRIPTION' | 'PREPAID';
export type BillingCadenceFreq = 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';

export const BILLING_MODE_OPTIONS: { value: BillingMode; label: string }[] = [
  { value: 'PER_VISIT', label: 'Per visit' },
  { value: 'SUBSCRIPTION', label: 'Subscription' },
  { value: 'PREPAID', label: 'Prepaid' },
];

export const BILLING_CADENCE_OPTIONS: { value: BillingCadenceFreq; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUALLY', label: 'Annually' },
];

export function normalizeJobTypeKey(value?: unknown) {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function resolveJobTypeValue(value: JobType | undefined, options: JobTypeOption[]): JobType {
  if (!value) return (options[0]?.value ?? 'SERVICE_CALL') as JobType;

  const exact = options.find((option) => option.value === value);
  if (exact) return exact.value;

  const targetKey = normalizeJobTypeKey(value);
  const normalized = options.find((option) =>
    [option.value, option.label, ...(option.aliases ?? [])].some(
      (candidate) => normalizeJobTypeKey(candidate) === targetKey,
    ),
  );

  return normalized?.value ?? value;
}

export function normalizeJobTypePriority(
  priority?: JobTypeSetting['defaultPriority'],
): JobPriority {
  if (priority === 'EMERGENCY') return 'URGENT';
  if (priority === 'LOW' || priority === 'NORMAL' || priority === 'HIGH') return priority;
  return 'NORMAL';
}

export function mapRecurrenceFrequency(frequency?: RecurrenceFrequency): RecurringFrequency {
  switch (frequency) {
    case 'DAY':
      return 'DAILY';
    case 'WEEK':
      return 'WEEKLY';
    case 'MONTH':
      return 'MONTHLY';
    case 'YEAR':
      return 'ANNUAL';
    default:
      return 'WEEKLY';
  }
}

export function normalizeRecurringConfigForJobForm(
  config?: RecurringConfig | null,
): RecurringConfig | null {
  if (!config) return null;
  const raw = config as RecurringConfig & Record<string, unknown>;
  const normalizeWeekdays = (value: unknown): number[] =>
    Array.isArray(value)
      ? Array.from(
          new Set(
            value
              .filter(
                (day) =>
                  typeof day === 'number' || (typeof day === 'string' && day.trim().length > 0),
              )
              .map(Number)
              .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6),
          ),
        )
      : [];
  const visitPatterns = Array.isArray(raw.visitPatterns)
    ? raw.visitPatterns.flatMap((value, index) => {
        if (!value || typeof value !== 'object') return [];
        const pattern = value as unknown as Record<string, unknown>;
        if (typeof pattern.startTime !== 'string' || typeof pattern.endTime !== 'string') return [];
        return [
          {
            ...pattern,
            id:
              typeof pattern.id === 'string' && pattern.id.trim()
                ? pattern.id
                : `legacy-visit-${index + 1}`,
            startTime: pattern.startTime,
            endTime: pattern.endTime,
          },
        ];
      })
    : [];
  const safeCollections = {
    weekDays: normalizeWeekdays(raw.weekDays),
    customDays: normalizeWeekdays(raw.customDays),
    visitPatterns: visitPatterns as RecurringConfig['visitPatterns'],
    blackoutDates: Array.isArray(raw.blackoutDates)
      ? raw.blackoutDates.filter((date): date is string => typeof date === 'string')
      : [],
  };
  const storedFrequencyValue = (config as unknown as Record<string, unknown>).frequency;
  const storedFrequency = typeof storedFrequencyValue === 'string' ? storedFrequencyValue : '';

  switch (storedFrequency) {
    case 'BIWEEKLY':
      return { ...config, ...safeCollections, frequency: 'WEEKLY', interval: 2 };
    case 'QUARTERLY':
      return { ...config, ...safeCollections, frequency: 'MONTHLY', interval: 3 };
    case 'SEMIANNUAL':
      return { ...config, ...safeCollections, frequency: 'MONTHLY', interval: 6 };
    case 'ANNUALLY':
      return { ...config, ...safeCollections, frequency: 'ANNUAL' };
    case 'DAILY':
    case 'WEEKLY':
    case 'MONTHLY':
    case 'ANNUAL':
    case 'CUSTOM':
      return { ...config, ...safeCollections, frequency: storedFrequency };
    default:
      return { ...config, ...safeCollections, frequency: 'WEEKLY' };
  }
}

export function resolveRecurringVisitPlacement(
  config?: RecurringConfig | null,
): RecurringVisitPlacement {
  if (
    config?.placement === 'SCHEDULE_NOW' ||
    config?.placement === 'ALL_DAY' ||
    config?.placement === 'ROUTE_BASED'
  ) {
    return config.placement;
  }
  if (config?.patternMode === 'FLEXIBLE') return 'ROUTE_BASED';
  return config?.visitGranularity === 'DAY_FLEXIBLE' ? 'ALL_DAY' : 'SCHEDULE_NOW';
}

/**
 * Replaces the `{{job_number}}` / `{job_number}` token with the real job
 * number once the server has assigned it. Mirrored on the backend so the
 * FE preview and persisted name agree.
 */
export function finalizeJobNumber(name: string, jobNumber: string): string {
  return finalizeNameTemplateToken(name, 'job_number', jobNumber);
}

export function generateJobNameFromTemplate({
  template,
  customerName,
  jobType,
  date,
  address,
}: JobNameTemplateInput) {
  const normalizedCustomer = customerName?.trim();
  const normalizedJobType = jobType?.trim();
  if (!normalizedCustomer || !normalizedJobType) return '';

  return renderNameTemplate(
    template?.trim() || DEFAULT_JOB_NAME_TEMPLATE,
    {
      customer_name: normalizedCustomer,
      job_type: normalizedJobType,
      date: date?.trim() ?? '',
      address: address?.trim() ?? '',
    },
    { preserveTokens: ['job_number'] },
  );
}

interface JobTypeDefaultsForm {
  jobType: JobType;
  jobTypeId?: string;
  jobTypeName?: string;
  priority: JobPriority;
  pipelineId: string;
  // NOT-362 — initial stage inherited from the JobType's `defaultStageId`.
  stageId: string;
  bookingType: 'ONE_OFF' | 'RECURRING';
  recurFrequency: RecurringFrequency;
  recurInterval?: string;
  recurStartDate?: string;
  invoicingMethod: InvoicingMethod;
  invoicingSchedule: InvoicingSchedule;
  invoicingMode: InvoicingMode;
  // NOT-362 — workflow defaults inherited from the JobType.
  autoInvoiceOnComplete: boolean;
  autoCharge: boolean;
  reviewRequestEnabled: boolean;
  visitCancellationWindowHours: string;
  visitCancellationFeeType: 'NONE' | 'FLAT' | 'PERCENT';
  visitCancellationFeeAmount: string;
  visitDepositRefundable: boolean;
}

export function applyJobTypeDefaults<T extends JobTypeDefaultsForm>(
  form: T,
  jobType: JobTypeSetting,
): T {
  const recurringDefaults = jobType.schedulingDefaults?.recurring;
  const recurrenceFrequency = recurringDefaults?.frequency ?? jobType.recurrenceFrequency;
  const recurrenceInterval = recurringDefaults?.interval ?? jobType.recurrenceInterval;
  const isRecurring = resolveJobTypeBookingType(jobType) === 'RECURRING';
  return {
    ...form,
    jobType: jobType.name,
    jobTypeId: jobType.id,
    jobTypeName: jobType.name,
    priority: normalizeJobTypePriority(jobType.defaultPriority),
    // A missing value is also part of the new Job Type's configuration. Never
    // retain template-owned values from the previously selected type.
    pipelineId: jobType.pipelineId ?? '',
    stageId: jobType.defaultStageId ?? '',
    bookingType: isRecurring ? 'RECURRING' : 'ONE_OFF',
    recurFrequency: isRecurring ? mapRecurrenceFrequency(recurrenceFrequency) : form.recurFrequency,
    recurInterval: isRecurring ? String(Math.max(1, recurrenceInterval ?? 1)) : form.recurInterval,
    recurStartDate:
      isRecurring && !form.recurStartDate ? dateToDateString(new Date()) : form.recurStartDate,
    // A JobType's explicit invoicing method wins over the pricing-model fallback.
    invoicingMethod:
      jobType.invoicingMethod ??
      (jobType.pricingModel === 'FLAT_RATE' ? 'FIXED_PRICE' : 'PER_VISIT'),
    invoicingSchedule: jobType.invoicingSchedule ?? 'ON_COMPLETION',
    invoicingMode: jobType.pricingModel === 'FLAT_RATE' ? 'FIXED_PRICE' : 'MANUAL',
    autoInvoiceOnComplete: jobType.autoInvoice ?? false,
    autoCharge: jobType.autoCharge ?? false,
    reviewRequestEnabled: jobType.reviewRequestDefault ?? false,
    visitCancellationWindowHours:
      jobType.cancellationWindowHours == null ? '' : String(jobType.cancellationWindowHours),
    visitCancellationFeeType: jobType.cancellationFeeType ?? 'NONE',
    visitCancellationFeeAmount:
      jobType.cancellationFeeAmount == null ? '' : String(jobType.cancellationFeeAmount),
    visitDepositRefundable: jobType.depositRefundable ?? true,
    // NOT — work-authorization / completion-sign-off defaults are NOT copied onto
    // the form here: the Advanced section tracks each as an `'inherit' | value`
    // tri-state that stays on `'inherit'` (resolved for display from the JobType)
    // until the user explicitly overrides it, so the DTO can omit inherited fields.
  } as T;
}

export function formatSavedPaymentMethodLabel(
  savedMethods: ContactPaymentMethod[],
  methodId: string,
) {
  const method = savedMethods.find((value) => value.id === methodId);
  if (!method) return methodId;
  // A bank account carries last4 and often a bank name in `brand`, so it has to
  // be recognised before the card branch or it reads as a card.
  if (isBankPaymentMethod(method)) {
    return `Bank account **** ${method.last4 ?? '0000'}`;
  }
  if (method.brand || method.last4 || method.type === 'CREDIT_CARD' || method.type === 'card') {
    const brand = method.brand?.toUpperCase() ?? 'CARD';
    const last4 = method.last4 ?? '0000';
    return `${brand} **** ${last4}`;
  }
  return method.id;
}

/**
 * What still blocks saving an edited job, or undefined when nothing does.
 *
 * Auto-charge is part of it: switched on with no method it saves an automation
 * that can never charge, and switching it on offers no method when every saved
 * one has been written off. Creating a job already refuses that.
 */
export function editJobRequiredAction(input: {
  name: string;
  jobTypeSelected: boolean;
  autoCharge: boolean;
  autoChargePaymentMethodId: string;
  /** A recurring series' cancellation fee terms; omitted for a one-off job. */
  seriesCancellationFee?: SeriesCancellationFeeTerms;
}): string | undefined {
  if (!input.name.trim()) return 'Enter job name';
  if (!input.jobTypeSelected) return 'Select job type';
  if (input.autoCharge && !input.autoChargePaymentMethodId) return 'Select payment method';
  if (input.seriesCancellationFee) {
    return seriesCancellationFeeRequiredAction(input.seriesCancellationFee);
  }
  return undefined;
}

export type SeriesCancellationFeePolicy = 'none' | 'flat' | 'minimum_term_remaining';

export interface SeriesCancellationFeeTerms {
  policy?: SeriesCancellationFeePolicy;
  minimumTermMonths?: number;
  cancellationFee?: number;
}

/**
 * A series' cancellation fee is only charged for a cancel inside its minimum
 * term, so a flat fee needs one as much as the remaining-term fee does. Without
 * it the fee was never charged, though the plan still named it.
 */
export function cancellationFeeNeedsMinimumTerm(
  policy: SeriesCancellationFeePolicy | undefined,
): boolean {
  return policy === 'flat' || policy === 'minimum_term_remaining';
}

/** What a series' cancellation fee still needs before it can be charged. */
export function seriesCancellationFeeRequiredAction(
  terms: SeriesCancellationFeeTerms,
): string | undefined {
  if (!cancellationFeeNeedsMinimumTerm(terms.policy)) return undefined;
  const months = terms.minimumTermMonths;
  if (months == null || !Number.isInteger(months) || months < 1) return 'Enter minimum term';
  if (terms.policy === 'flat' && !((terms.cancellationFee ?? 0) > 0)) {
    return 'Enter cancellation fee';
  }
  return undefined;
}
